import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchNews,
  addNews,
  updateNews,
  updateUserPreference,
  deleteNews,
  activateDeactivateNews,
  fetchNewsTableColumnNames,
} from "../../../slices/thunks";
import { getAuthUser } from "../../../helpers/utils";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import DeleteModal from "../../../Components/Common/DeleteModal";
import CommonAddModal from "../../../Components/Common/Modal/CommonAddModal";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import usePermissions from "../../../Components/Hooks/usePermissions";
import NewsForm from "../../../Components/Common/AddNewsForm/AddNewsForm";
import { useNavigate } from "react-router-dom";

const labels = [
  {
    id: 1,
    labelName: "Link",
    fieldName: "text-box",
    name: "Link",
    value: "Link",
    required: true,
    errorMessage: "LinkRequired",
  },
  {
    id: 2,
    labelName: "language",
    fieldName: "select",
    name: "Language",
    value: "Language",
    required: true,
    placeholder: "SelectLanguage",
    errorMessage: "LanguageRequired",
    options: [
      { value: 'ar', label: "Arabic" },
      { value: 'en', label: "English" },
    ]
  },
  {
    id: 3,
    labelName: "Schedule",
    fieldName: "select",
    name: "Schedule",
    value: "Schedule",
    required: true,
    placeholder: "SelectSchedule",
    errorMessage: "ScheduleRequired",
    options: [
      { value: 1, label: "Daily" },
      // { value: 2, label: "Weekly" },
      // { value: 3, label: "Monthly" }
    ]
  },
  {
    id: 2,
    labelName: "Pattern",
    fieldName: "select",
    name: "Pattern",
    value: "Pattern",
    required: true,
    placeholder: "SelectTime",
    errorMessage: "TimeRequired",
    options: [
      { value: '0 */1 * * *', label: "1 Hour" },
      { value: '0 */2 * * *', label: "2 Hours" },
      { value: '0 */3 * * *', label: "3 Hours" },
      { value: '0 */4 * * *', label: "4 Hours" },
      { value: '0 */5 * * *', label: "5 Hours" },
      { value: '0 */6 * * *', label: "6 Hours" },
      { value: '0 */7 * * *', label: "7 Hours" },
      { value: '0 */8 * * *', label: "8 Hours" },
      { value: '0 */9 * * *', label: "9 Hours" },
      { value: '0 */10 * * *', label: "10 Hours" },
      { value: '0 */11 * * *', label: "11 Hours" },
      { value: '0 */12 * * *', label: "12 Hours" },
      { value: '0 0 0 * * *', label: "24 Hours" },
    ]
  },
];
 
const actionConfig = {
  edit: {
    icon: "ri-edit-2-line",
    tooltip: "Edit",
  },
  delete: {
    icon: "ri-delete-bin-2-line",
    tooltip: "Delete",
  },
};

function News() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { user } = getAuthUser();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddOrEdit, setIsAddOrEdit] = useState("isAdd");
  const [selectedRow, setSelectedRow] = useState();
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);

  const { News, loading, tableColumnNames } = useSelector(
    (state) => ({
      tableColumnNames: state.News.tableColumnNames,
      loading: state.News.loading,
      News: state.News.news,
    })
  );

  const handleClassesClicks = (value) => {
    setShow(true);
    setIsAddOrEdit(value);
    setSelectedRow({});
  };

  const onEditClickHandler = (user, value) => {
    setSelectedRow(user);
    setIsAddOrEdit(value);
    setShow(true);
  };
    

  const onSubmitHandler = (values, { resetForm }) => {
    // Prepare the payload
    const newsObj = {
      Link: values.Link.trim(),
      Language: values.Language,
      Schedule: values.Schedule,  
      Pattern: values.Pattern
    };

    if (isAddOrEdit === "isEdit" && selectedRow) {
      newsObj["ID"] = selectedRow.ID;
    }

    if (isAddOrEdit === "isAdd") {
      dispatch(addNews(newsObj));
    } else {
      dispatch(updateNews(newsObj));
    }
    resetForm();
    setShow(false);
  };

  const saveUserPreferences = async () => {
    const preferencesPayload = {
      ColumnIDs: tableColumnNames.reduce((acc, column) => {
        acc[column.ID] = selectedColumns.includes(column.ID);
        return acc;
      }, {}),
    };

    setModalOpen(false);
    try {
      await dispatch(updateUserPreference(preferencesPayload)).unwrap();
      await dispatch(
        fetchNewsTableColumnNames({
          module: "NEWS",
        })
      );

    } catch (error) {
      console.error("Error updating preferences:", error);
    }
  };

  const confirmDelete = () => {
    dispatch(deleteNews({ IDs: selectedIds }))
      .then(() => {
        setSelectedIds([]);
      })
      .catch((error) => {
        console.error("Error deleting:", error);
      });
    setDeleteModal(false);
  };

  const onActiveOrDeactiveChange = (item, e) => {
    dispatch(activateDeactivateNews({ ID: item.ID }))
  };

  // Compute permission
  const permission = (() => {
    return usePermissions(1, 17);
  })();


  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(
          fetchNewsTableColumnNames({
            module: "NEWS",
          })
        ).unwrap();
        await dispatch(fetchNews());
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [dispatch]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <BreadCrumb title={t("News")} />
          </Col>
        </Row>

        <div className="table-responsive">
          <Card>
            <CardHeader>
                <Row className="g-4 ">
                  <Col className="col-sm-auto">
                    <div>
                      <Button
                        className="btn btn-soft-danger ms-2 me-2"
                        onClick={() => setDeleteModal(true)}
                        disabled={selectedIds.length === 0}
                      >
                        <i className="ri-delete-bin-2-line"></i>
                      </Button>
                      {permission.CanAdd && (
                        <Button
                          color="secondary"
                          className="add-btn me-1"
                          onClick={() => handleClassesClicks("isAdd")}
                          id="create-btn"
                          disabled={loading}
                        >
                          <i className="ri-add-line align-bottom me-1 ms-1"></i>
                          {t("AddNew")}
                        </Button>
                      )}


                      <Button
                            color="secondary"
                            className="add-btn me-1"
                            onClick={() => navigate("/administration/news-data")}
                        id="create-btn"
                      >
                        <i className="ri-newspaper-line align-bottom me-1"></i> {/* Icon for upload */}
                        {t("News Data")}
                      </Button>

                      {/* <Button
                        color="secondary"
                        className="add-btn me-1"
                        onClick={() => navigate("/administration/news-data-scraper")}
                        id="create-btn"
                      >
                        <i className="ri-upload-cloud-2-line align-bottom me-1"></i>
                        {t("News Data Scraper")}
                      </Button> */}
                     
                    </div>
                  </Col>
                  <Col className="col-sm">
                    <div className="d-flex justify-content-sm-end">
                      <Button
                        className="btn btn-soft"
                        onClick={() => setModalOpen(true)}
                        disabled={loading}
                      >
                        <i className="ri-filter-3-line"></i>
                      </Button>
                      <div className="search-box ms-2 me-2">
                        <Input
                          type="text"
                          className="form-control search"
                          placeholder={t("Search")}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </div>
                  </Col>
                </Row>
            </CardHeader>

            <CardBody>
              <div className="listjs-table" id="customerList">
             
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center">
                    <Spinner
                      style={{ height: "3rem", width: "3rem" }}
                      className="me-2"
                    >
                      Loading...
                    </Spinner>
                  </div>
                ) : (
                  <div className="listjs-table" id="customerList">
                     <CommonDataTable
                      data={News}
                      tableColumnNames={tableColumnNames}
                      searchTerm={searchTerm}
                      onEditClickHandler={onEditClickHandler}
                      onActiveOrDeactiveChange={onActiveOrDeactiveChange}
                      setDeleteModal={setDeleteModal}
                      setSelectedIds={setSelectedIds}
                      actionConfig={actionConfig}
                      permission={permission}
                    />
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </Container>

      {/* Modal  */}
      <CommonAddModal
        show={show}
        setShow={setShow}
        onSubmitHandler={onSubmitHandler}
        labels={labels}
        isAddOrEdit={isAddOrEdit}
        selectedItemData={selectedRow}
        FormBody={NewsForm}
        title={isAddOrEdit === "isAdd" ? t("AddNews") : t("EditNews")}
        size="lg"
      />

      {/* ColumnSelectionModal */}
      <ColumnSelectionModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        columns={tableColumnNames}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        handleSave={saveUserPreferences}
      />

      {/* Delete modal  */}
      <DeleteModal
        showDelete={deleteModal}
        setShowDelete={setDeleteModal}
        onDeleteClick={confirmDelete}
        setSelectedIds={setSelectedIds}
        description={t("confirmDelete")}
      />

    </div>
  );
}

export default News;
